import React, { useMemo, useState, useEffect } from 'react';
import { GridPageChangeEvent } from '@progress/kendo-react-grid';
import { NumericTextBox } from '@progress/kendo-react-inputs';
import GridFE from '@components-clean/core/grid-fe';
import { MultiValueDataCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import Toggle from '@components-clean/core/toggle';

interface Page {
    skip: number;
    take: number;
}

interface MultiValueCell {
    comparisonValue: string;
    valueInFocus: string;
    changeDirection: number;
    comparisonColour: number;
    colourInFocus: number;
}

interface DataRow {
    [key: string]: string | MultiValueCell;
}

const VirtualizedGridPage = () => {
    const [gridKey, setGridKey] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState<Page>({ skip: 0, take: 60 });
    const [numberOfColumns, setNumberOfColumns] = useState<number>(100);
    const [numberOfRows, setNumberOfRows] = useState<number>(100);
    const [virtualizationEnabled, setVirtualizationEnabled] = useState<boolean>(true);
    const [displayComplexCells, setDisplayComplexCells] = useState<boolean>(false);
    const [data, setData] = useState<DataRow[]>([]);

    const getLeafColumns = (columns: GridColumnProps[]): GridColumnProps[] => {
        const leaves: GridColumnProps[] = [];
        columns.forEach((col) => {
            if (col.children && col.children.length > 0) {
                leaves.push(...getLeafColumns(col.children));
            } else {
                leaves.push(col);
            }
        });
        return leaves;
    };

    const getColumns = (): GridColumnProps[] => {
        const maxDepth = 3;
        const maxChildCount = 4;

        const columns: GridColumnProps[] = [];
        let colIdx = 1;

        const stack: Array<{ column: GridColumnProps; isLeaf: boolean; depth: number }> = [
            {
                column: {
                    field: 'Field-1',
                    width: 150,
                    cells: {
                        data: displayComplexCells
                            ? (gridCellProps) =>
                                  MultiValueDataCell({
                                      ...gridCellProps,
                                      highlighted: true,
                                  })
                            : undefined,
                    },
                    children: [],
                },
                isLeaf: true,
                depth: 1,
            },
        ];

        while (stack.length > 0) {
            const { column, isLeaf, depth } = stack.pop()!;
            if (colIdx <= numberOfColumns) {
                const childCount = Math.ceil(Math.random() * maxChildCount);

                if (childCount === 1 || depth > maxDepth || colIdx === numberOfColumns) {
                    colIdx++;
                    const nextColumn: GridColumnProps = {
                        field: `Field-${colIdx}`,
                        width: 150,
                        children: [],
                        cells: {
                            data: displayComplexCells
                                ? (gridCellProps) =>
                                      MultiValueDataCell({
                                          ...gridCellProps,
                                          highlighted: true,
                                      })
                                : undefined,
                        },
                    };
                    stack.push({
                        column: nextColumn,
                        isLeaf: true,
                        depth: 1,
                    });
                } else {
                    for (let i = 0; i < childCount; i++) {
                        colIdx++;
                        if (colIdx <= numberOfColumns) {
                            const nextColumn: GridColumnProps = {
                                field: `Field-${colIdx}`,
                                width: 150,
                                children: [],
                                cells: {
                                    data: displayComplexCells
                                        ? (gridCellProps) =>
                                              MultiValueDataCell({
                                                  ...gridCellProps,
                                                  highlighted: true,
                                              })
                                        : undefined,
                                },
                            };
                            column.children?.push(nextColumn);
                            stack.push({
                                column: nextColumn,
                                isLeaf: false,
                                depth: depth + 1,
                            });
                        }
                    }
                }
                if (isLeaf) {
                    columns.push(column);
                }
            }
        }

        return columns;
    };

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    const columns: GridColumnProps[] = useMemo(() => getColumns(), [numberOfColumns, displayComplexCells]);
    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    const leafColumns = useMemo(() => getLeafColumns(columns), [columns]);

    const getData = (skip: number, take: number): DataRow[] => {
        const rows: DataRow[] = [];
        for (let r = skip + 1; r <= skip + take && r <= numberOfRows; r++) {
            const row: DataRow = { id: r.toString() }; // Use row number as unique id
            leafColumns.forEach((col, idx) => {
                if (col.field) {
                    row[col.field] = displayComplexCells
                        ? {
                              comparisonValue: `R${r}:C${idx + 1}`,
                              valueInFocus: `R${r}:C${idx + 1}`,
                              changeDirection: Math.floor(Math.random() * 3), // Random ScoreDirection (0, 1, or 2)
                              comparisonColour: Math.floor(Math.random() * 4) + 1, // Random ScoreColor (1-4)
                              colourInFocus: Math.floor(Math.random() * 4) + 1, // Random ScoreColor (1-4),
                          }
                        : `R${r}:C${idx + 1}`;
                }
            });
            rows.push(row);
        }
        return rows;
    };

    const updatePageAndData = () => {
        if (virtualizationEnabled) {
            setPage({ skip: 0, take: 60 });
        } else {
            setPage({ skip: 0, take: numberOfRows });
        }
        const gridData = virtualizationEnabled ? getData(page.skip, page.take) : getData(0, numberOfRows);
        setData(gridData);
        setLoading(false);
    };

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setLoading(true);
        if (numberOfColumns || displayComplexCells) {
            // eslint-disable-next-line @eslint-react/web-api-no-leaked-timeout
            setTimeout(() => {
                setGridKey((prev) => prev + 1);
                updatePageAndData();
            }, 0);
        } else {
            // eslint-disable-next-line @eslint-react/web-api-no-leaked-timeout
            setTimeout(() => {
                updatePageAndData();
            }, 0);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [numberOfRows, numberOfColumns, virtualizationEnabled, displayComplexCells]);

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setLoading(true);
    }, [numberOfRows, numberOfColumns, virtualizationEnabled, displayComplexCells]);

    useEffect(() => {
        if (loading) {
            const id = requestAnimationFrame(() => setLoading(false));
            return () => cancelAnimationFrame(id);
        }
    }, [loading]);

    const pageChange = (event: GridPageChangeEvent) => {
        setPage(event.page);
    };

    const virtualizationProps = virtualizationEnabled
        ? {
              columnVirtualization: true,
              pageSize: page.take,
              skip: page.skip,
              total: numberOfRows,
              onPageChange: pageChange,
              scrollable: 'virtual' as const,
              rowHeight: 28,
          }
        : {};

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <span>
                    <NumericTextBox
                        name="numberOfColumnsInput"
                        data-testid="number-of-columns-input"
                        label="Columns"
                        onChange={(e) => setNumberOfColumns(Number(e.target.value))}
                        min={1}
                        value={numberOfColumns}
                    />
                </span>
                <span style={{ marginLeft: 8 }}>
                    <NumericTextBox
                        name="numberOfRowsInput"
                        data-testid="number-of-rows-input"
                        label="Rows"
                        onChange={(e) => setNumberOfRows(Number(e.target.value))}
                        min={1}
                        value={numberOfRows}
                    />
                </span>
                <span style={{ marginLeft: 8 }}>
                    <Toggle
                        name="virtualizationToggle"
                        data-testid="virtualization-toggle"
                        defaultChecked
                        onChange={(e) => setVirtualizationEnabled(Boolean(e.target.value))}
                    >
                        Toggle Virtualization
                    </Toggle>
                </span>
                <span style={{ marginLeft: 8 }}>
                    <Toggle
                        name="displayComplexCellsToggle"
                        data-testid="display-complex-cells-toggle"
                        defaultChecked={false}
                        onChange={(e) => setDisplayComplexCells(Boolean(e.target.value))}
                    >
                        Display Complex Cells
                    </Toggle>
                </span>
            </div>
            <GridFE
                key={gridKey}
                dataItemKey="id"
                title="Virtualized Grid"
                maxHeight="800px"
                testDataId="virtualized-grid"
                data={data}
                columns={columns}
                useLoadingOverlay
                reorderable={false}
                gridSettingsMenuDisabled
                loading={loading}
                {...virtualizationProps}
            />
        </>
    );
};

export default VirtualizedGridPage;
